import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'

import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  name: string
  translations: FormTranslations
}

export function useBanner(id?: number) {
  const [createBanner, { loading: createLoading }] = useMutation(
    AdminAPI.banner.CREATE,
    {
      update(cache, { data, errors }) {
        if (errors) return
        if (!data?.createBanner) return

        const banner = getFragment(
          SharedAPI.banner.BANNER_FRAGMENT,
          data.createBanner
        )

        cache.updateQuery({ query: AdminAPI.banner.GET_ALL }, data => {
          return {
            getAllBanner: [...(data?.getAllBanner ?? []), banner]
          }
        })
      }
    }
  )

  const [updateBanner, { loading: updateLoading }] = useMutation(
    AdminAPI.banner.UPDATE
  )

  const [deleteBanner, { loading: deleteLoading }] = useMutation(
    AdminAPI.banner.DELETE,
    {
      update(cache, { data }) {
        if (!data) return

        const banner = getFragment(
          SharedAPI.banner.BANNER_FRAGMENT,
          data.deleteBanner
        )

        cache.updateQuery({ query: AdminAPI.banner.GET_ALL }, data => {
          const banners = getFragment(
            SharedAPI.banner.BANNER_FRAGMENT,
            data?.getAllBanner
          )

          return {
            getAllBanner: banners?.filter(b => {
              return banner.id !== b.id
            })
          }
        })
      }
    }
  )

  const create = useCallback(
    (data: FormData) => {
      createBanner({
        variables: {
          input: {
            name: data.name,
            translations: formToGraphql(data.translations)
          }
        }
      })
    },
    [createBanner]
  )

  const update = useCallback(
    (data: FormData) => {
      if (!id) return

      updateBanner({
        variables: {
          input: {
            id,
            ...data,
            translations: formToGraphql(data.translations)
          }
        }
      })
    },
    [id, updateBanner]
  )

  const remove = useCallback(() => {
    if (!id) return

    deleteBanner({
      variables: {
        id
      }
    })
  }, [id, deleteBanner])

  return {
    create,
    update,
    remove,
    createLoading,
    updateLoading,
    deleteLoading
  }
}
